import { z } from "zod";
import {
  acceptedContractEnvelopeSchema,
  canonicalProtocolIdSetSchema,
  codingTaskRequirementSchema,
  compilationRecordContentSchema,
  canonicalJsonSchema,
  contractAmendmentDecisionSchema,
  contractAmendmentSchema,
  contractContentSchema,
  contractSourceSchema,
  completionIntentSchema,
  completionObligationClassificationSchema,
  enforcementDecisionSchema,
  enforcementEffectSchema,
  enforcementPolicySchema,
  learningProposalSchema,
  learningProposalTargetSchema,
  machineReadableVerdictReceiptSchema,
  railActionIntentSchema,
  reviewRequestSchema,
  reviewResolutionSchema,
  protocolIdSchema,
  protocolTimestampSchema,
  sha256DigestSchema,
  signedProviderEnrollmentServiceRequestSchema,
  signedProviderEnrollmentServiceResponseSchema,
  universalWorkIdSchema,
  universalWorkItemSchema,
  workAttemptSchema,
  repositorySelectionBindingSchema,
} from "@excellent/core/verification-protocol-client";
import {
  universalConsoleOverviewSchema,
  universalReceiptDetailSchema,
  universalTrustMaterialSchema,
  universalWorkDetailSchema,
  universalVerifyCommandSchema,
  universalVerifyResponseSchema,
  railAdapterKindSchema,
  railVerificationSignalSchema,
} from "@excellent/core/verification-universal-surfaces-client";
import { universalAdapterDeliverySchema } from "@excellent/core/verification-universal-adapter-delivery";
import { environmentUiContextSubjectSchema } from "./environments";
import type { Operation } from "../types";

export const universalWorkEnvironmentQuerySchema = z
  .object({
    originTenantId: environmentUiContextSubjectSchema.shape.originTenantId,
    environmentId: environmentUiContextSubjectSchema.shape.environmentId,
    generationId: environmentUiContextSubjectSchema.shape.generationId,
  })
  .strict();

/**
 * Transitional compatibility input for callers that have not moved to the
 * canonical Environment tuple. The server resolves this adapter to exactly one
 * current local Environment; it is never treated as Environment authority.
 */
export const universalWorkLegacyEnvironmentQuerySchema = z
  .object({ env: z.enum(["demo", "real"]).optional() })
  .strict();

export const universalWorkReadQuerySchema = z.union([
  universalWorkEnvironmentQuerySchema,
  universalWorkLegacyEnvironmentQuerySchema,
]);
export type UniversalWorkReadQuery = z.infer<typeof universalWorkReadQuerySchema>;

export const universalWorkEnvironmentReadScopeSchema = z
  .object({
    schemaId: z.literal("excellent.universal-work.environment-read-scope.v1"),
    mode: z.enum(["canonical-environment", "legacy-real-adapter", "legacy-demo-adapter"]),
    subject: environmentUiContextSubjectSchema,
    locality: z.enum(["local", "remote", "offline-snapshot"]),
    authorityBindingDigest: sha256DigestSchema,
    validUntil: protocolTimestampSchema,
  })
  .strict();
export type UniversalWorkEnvironmentReadScope = z.infer<
  typeof universalWorkEnvironmentReadScopeSchema
>;

type UniversalWorkScopedResponse<T> = T & {
  environmentReadScope: UniversalWorkEnvironmentReadScope;
};

function universalWorkScopedResponse<T extends z.ZodTypeAny>(
  base: T,
): z.ZodType<UniversalWorkScopedResponse<z.output<T>>> {
  return z.any().transform((value, context) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: "Expected scoped Work response" });
      return z.NEVER;
    }
    const { environmentReadScope, ...baseValue } = value as Record<string, unknown>;
    const parsedBase = base.safeParse(baseValue);
    const parsedScope = universalWorkEnvironmentReadScopeSchema.safeParse(environmentReadScope);
    if (!parsedBase.success || !parsedScope.success) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Invalid Environment-scoped Work response",
      });
      return z.NEVER;
    }
    return { ...parsedBase.data, environmentReadScope: parsedScope.data };
  }) as z.ZodType<UniversalWorkScopedResponse<z.output<T>>>;
}

export const UNIVERSAL_VERIFICATION_OVERVIEW_DESCRIPTION =
  "Prospective completion authority is returned in current; retrospective and legacy receipts plus all learning proposals remain readable only in incomplete history and cannot authorize PASS. Top-level completion arrays exactly mirror current and reject historical records. Adapter guarantees are effective only after proven live mediated delivery.";
export const universalVerificationOverviewResponseSchema = universalWorkScopedResponse(
  universalConsoleOverviewSchema,
).describe(UNIVERSAL_VERIFICATION_OVERVIEW_DESCRIPTION);
export const UNIVERSAL_VERIFICATION_WORK_DETAIL_DESCRIPTION =
  "Canonical Environment-bound Work detail. Action authority is derived only from exact current Contract, Attempt, and prospective Receipt ancestry; incomplete history stays readable but non-authoritative.";
export const universalVerificationWorkDetailResponseSchema = universalWorkScopedResponse(
  universalWorkDetailSchema,
).describe(UNIVERSAL_VERIFICATION_WORK_DETAIL_DESCRIPTION);

export const universalVerificationReceiptDetailResponseSchema = universalWorkScopedResponse(
  universalReceiptDetailSchema,
).describe("Canonical receipt detail bound to one exact current Environment.");

const receiptParams = z.object({ id: z.string().trim().min(1).max(200) }).strict();
// The Work id grammar is the protocol's own (`universalWorkIdSchema`), not a
// route-local narrowing of it. The previous `work:<uuid>` regex admitted only
// one minter's shape and so answered 400 for the canonical structured ids this
// product actually stores — the tenant's own records, refused at the door.
export const universalWorkDetailParamsSchema = z.object({ id: universalWorkIdSchema }).strict();
const idempotencyField = { idempotencyKey: protocolIdSchema } as const;

export const universalCreateWorkBodySchema = universalWorkItemSchema
  .pick({
    projectId: true,
    workType: true,
    title: true,
    description: true,
    completionIntent: true,
    consequence: true,
    source: true,
  })
  .extend({ completionIntent: completionIntentSchema, ...idempotencyField })
  .strict();

export const universalAcceptContractBodySchema = z
  .object({
    ...idempotencyField,
    content: contractContentSchema,
    compilation: compilationRecordContentSchema.omit({ admittedContractDigest: true }),
    authorityBasis: z.enum(["requester", "policy", "named_human"]),
    deploymentMode: z.enum(["REPLAY", "SHADOW", "REVIEW_GATED", "ENFORCED", "PAUSED"]),
    expectedWorkerActorId: z.string().trim().min(1).max(200),
    sourceReview: z
      .object({
        reviewId: protocolIdSchema,
        reviewedAt: protocolTimestampSchema,
        reviewDigest: sha256DigestSchema,
      })
      .strict(),
  })
  .strict();

const universalCompileContractObligationSchema = z
  .object({
    id: z.string().trim().min(1).max(200),
    classification: completionObligationClassificationSchema,
    sourceIds: canonicalProtocolIdSetSchema.optional(),
    coversOutcomeIds: canonicalProtocolIdSetSchema,
    subject: z.string().trim().min(1).max(200),
    predicate: z
      .object({
        kind: z.string().trim().min(1).max(200),
        description: z.string().trim().min(1),
        parameters: z.record(canonicalJsonSchema),
      })
      .strict(),
    successCondition: z.string().trim().min(1).nullable(),
    falsifier: z.string().trim().min(1).nullable(),
    required: z.boolean(),
    evidence: z
      .array(
        z
          .object({
            kind: z.string().trim().min(1).max(200),
            minimumCount: z.number().int().positive(),
            maximumAgeSeconds: z.number().int().nonnegative().nullable(),
            requiresIndependentCollection: z.boolean(),
          })
          .strict(),
      )
      .min(1),
    evaluator: z
      .object({
        ref: z.string().trim().min(1).max(200),
        version: z.string().trim().min(1).max(100),
        digest: sha256DigestSchema,
        authority: z.enum(["deterministic", "semantic", "human", "imported"]),
        rubricDigest: sha256DigestSchema.nullable(),
        requiresIndependentExecution: z.boolean(),
      })
      .strict()
      .optional(),
    oracles: z
      .array(
        z
          .object({
            ref: z.string().trim().min(1).max(200),
            version: z.string().trim().min(1).max(100),
            digest: sha256DigestSchema,
            required: z.boolean(),
          })
          .strict(),
      )
      .optional(),
  })
  .strict();

export const universalCompileContractBodySchema = z
  .object({
    ...idempotencyField,
    workItemId: z.string().trim().min(1).max(200),
    version: z.string().trim().min(1).max(100),
    expectedWorkerActorId: z.string().trim().min(1).max(200),
    deploymentMode: z.enum(["REPLAY", "SHADOW", "REVIEW_GATED", "ENFORCED", "PAUSED"]),
    sources: z.array(contractSourceSchema).min(1).optional(),
    obligations: z.array(universalCompileContractObligationSchema).min(1),
    sourceReview: z
      .object({
        reviewId: protocolIdSchema,
        reviewedAt: protocolTimestampSchema,
        reviewDigest: sha256DigestSchema,
      })
      .strict(),
  })
  .strict();

const universalRepositoryBaselineSchema = z
  .object({
    kind: z.enum(["git_commit", "tree_snapshot"]),
    revision: z.string().trim().min(1),
    treeDigest: sha256DigestSchema,
    capturedAt: protocolTimestampSchema,
  })
  .strict();

export const universalCodingRepositoryAdmissionSchema = z
  .object({
    selectionId: protocolIdSchema,
    repositoryId: protocolIdSchema,
    hostAdmissionDigest: sha256DigestSchema,
    admissionRevision: z.string().trim().min(1),
  })
  .strict();

export const universalCodingIntakeBodySchema = z
  .object({
    ...idempotencyField,
    projectId: z.string().trim().min(1).max(200).nullable(),
    workType: protocolIdSchema,
    title: z.string().trim().min(1).max(300),
    taskText: z.string().trim().min(1),
    taskUri: z.string().trim().min(1).max(500),
    consequence: z.enum(["low", "medium", "high", "critical"]),
    requirements: z.array(codingTaskRequirementSchema).min(1),
    unresolvedDecisions: z.array(z.string().trim().min(1)).optional(),
    allowedPaths: z.array(z.string().trim().min(1)).min(1),
    version: z.string().trim().min(1).max(100),
    deploymentMode: z.enum(["REPLAY", "SHADOW", "REVIEW_GATED", "ENFORCED", "PAUSED"]),
    expectedWorkerActorId: z.string().trim().min(1).max(200),
    repositorySelectionId: protocolIdSchema,
    expectedRepositoryAdmission: universalCodingRepositoryAdmissionSchema,
    repositoryWitness: z
      .object({ path: z.string().trim().min(1), digest: sha256DigestSchema })
      .strict()
      .optional(),
  })
  .strict();

export const universalCodingIntakeResponseSchema = z
  .object({
    work: universalWorkItemSchema,
    contract: acceptedContractEnvelopeSchema,
    intakeDigest: sha256DigestSchema,
    repositoryId: protocolIdSchema,
    repositoryMapDigest: sha256DigestSchema,
    repositorySnapshotDigest: sha256DigestSchema,
    baseline: universalRepositoryBaselineSchema,
    repositoryBinding: repositorySelectionBindingSchema,
  })
  .strict();

export const universalCodingRepositorySelectionSchema = universalCodingRepositoryAdmissionSchema;
export const universalCodingRepositorySelectionsResponseSchema = z
  .object({ selections: z.array(universalCodingRepositorySelectionSchema) })
  .strict();

export const universalAcceptSourceReviewBodySchema = z
  .object({
    ...idempotencyField,
    workItemId: z.string().trim().min(1).max(200).optional(),
    work: universalWorkItemSchema
      .pick({
        projectId: true,
        workType: true,
        title: true,
        description: true,
        consequence: true,
      })
      .partial()
      .strict()
      .optional(),
    version: z.string().trim().min(1).max(100),
    expectedWorkerActorId: z.string().trim().min(1).max(200),
    deploymentMode: z.enum(["REPLAY", "SHADOW", "REVIEW_GATED", "ENFORCED", "PAUSED"]),
    openAttempt: z
      .object({
        inputSnapshotDigest: sha256DigestSchema,
        idempotencyKey: protocolIdSchema.optional(),
        supersedesAttemptId: z.string().trim().min(1).max(200).nullable().optional(),
      })
      .strict()
      .optional(),
    scenario: z.enum([
      "ambiguous-production-ready",
      "concrete-retry-resume",
      "conflicting-policy",
      "revoked-grant",
      "workflow-template-observed-occurrence",
    ]),
    reviewId: protocolIdSchema,
    expectedReviewDigest: sha256DigestSchema,
    expectedCompilationDigest: sha256DigestSchema,
  })
  .strict();

export const universalOpenAttemptBodySchema = z
  .object({
    ...idempotencyField,
    contractId: z.string().trim().min(1).max(200),
    contractVersion: z.string().trim().min(1).max(100),
    workerActorId: z.string().trim().min(1).max(200),
    inputSnapshotDigest: sha256DigestSchema,
    supersedesAttemptId: z.string().trim().min(1).max(200).nullable().optional(),
  })
  .strict();

export const universalSubmitAttemptBodySchema = z
  .object({
    ...idempotencyField,
    attemptId: z.string().trim().min(1).max(200),
    outputSnapshotDigest: sha256DigestSchema,
  })
  .strict();

export const universalIssueWorkerDelegationBodySchema = z
  .object({
    consent: z.literal(true),
    contractId: protocolIdSchema,
    contractVersion: protocolIdSchema,
    expectedContractDigest: sha256DigestSchema,
    registeredWorkerActorId: protocolIdSchema,
    environmentBindingDigest: sha256DigestSchema,
    expiresAt: protocolTimestampSchema,
    supersedesAttemptId: protocolIdSchema.optional(),
  })
  .strict();

export const universalWorkerEnrollmentsResponseSchema = z
  .object({
    tenantId: protocolIdSchema,
    workers: z.array(
      z
        .object({
          actorId: protocolIdSchema,
          tenantId: protocolIdSchema,
          enrollmentStatus: z.enum(["active", "revoked", "conflicting"]),
          enrolledAt: z.string(),
          declaredExecutionBoundaryId: protocolIdSchema,
          recordedDelegationCount: z.number().int().nonnegative(),
          recordedAttemptCount: z.number().int().nonnegative(),
          environmentAttested: z.literal(false),
          executionProven: z.literal(false),
        })
        .strict(),
    ),
  })
  .strict();
export type UniversalWorkerEnrollments = z.infer<typeof universalWorkerEnrollmentsResponseSchema>;

export const universalRegisterWorkerPrincipalBodySchema = z
  .object({
    consent: z.literal(true),
    workerActorId: protocolIdSchema,
    executionBoundaryId: protocolIdSchema,
    idempotencyKey: protocolIdSchema,
  })
  .strict();

export const universalRegisterWorkerPrincipalResponseSchema = z
  .object({
    actorId: protocolIdSchema,
    tenantId: protocolIdSchema,
    roles: z.tuple([z.literal("worker")]),
    capabilities: z.tuple([z.literal("attempt:execute")]),
    executionBoundaryId: protocolIdSchema,
    environmentAttested: z.literal(false),
    executionPerformed: z.literal(false),
  })
  .strict();

export const universalIssueWorkerDelegationResponseSchema = z
  .object({
    bearer: z.string().regex(/^ewg_[a-f0-9]{32}\.[a-f0-9]{64}$/),
    grantDigest: sha256DigestSchema,
    environmentAttested: z.literal(false),
    executionPerformed: z.literal(false),
  })
  .strict();

export const universalWorkerAttachAttemptBodySchema = z
  .object({
    idempotencyKey: protocolIdSchema,
    contractId: protocolIdSchema,
    contractVersion: protocolIdSchema,
    inputSnapshotDigest: sha256DigestSchema,
    supersedesAttemptId: protocolIdSchema.nullable().optional(),
  })
  .strict();

export const universalWorkerSubmitAttemptBodySchema = z
  .object({
    idempotencyKey: protocolIdSchema,
    attemptId: protocolIdSchema,
    outputSnapshotDigest: sha256DigestSchema,
  })
  .strict();

export const universalWorkerAttemptCommandResponseSchema = z
  .object({
    attempt: workAttemptSchema,
    replayed: z.boolean(),
    executionPerformed: z.literal(false),
  })
  .strict();

/**
 * Service-owned evidence submission. The authenticated caller selects only
 * the submitted Attempt and the approved host check for each frozen command;
 * contract/evidence/Environment/policy/verifier/signing bytes are deliberately
 * absent and are resolved by the server.
 */
export const universalCodingEvidenceReplayBodySchema = z
  .object({
    idempotencyKey: protocolIdSchema,
    attemptId: protocolIdSchema,
    selections: z
      .array(
        z
          .object({
            obligationId: protocolIdSchema,
            checkId: protocolIdSchema,
          })
          .strict(),
      )
      .min(1)
      .max(32),
  })
  .strict();

export const universalExecuteRailBodySchema = z
  .object({
    ...idempotencyField,
    adapter: railAdapterKindSchema,
    receiptId: z.string().trim().min(1).max(200),
    policy: enforcementPolicySchema,
    target: z.string().trim().min(1),
  })
  .strict();

export const universalProposeAmendmentBodySchema = z
  .object({
    ...idempotencyField,
    baseContractId: protocolIdSchema,
    baseContractVersion: z.string().trim().min(1).max(100),
    proposedContent: contractContentSchema,
    proposedDiff: canonicalJsonSchema,
    rationale: z.string().trim().min(1),
    impactedObligationIds: z.array(protocolIdSchema),
    caseImpact: z
      .object({
        // Four, not five: this is the amendment proposer's SUBMITTED caseImpact,
        // and a proposer may not author an accusation. Lockstep with
        // `protocol/contract.ts` and `worker-amendments.ts` in core.
        status: z.enum(["PASS", "FAIL", "UNKNOWN", "NOT_RUN"]),
        digest: sha256DigestSchema,
        reasonCodes: z.array(protocolIdSchema),
      })
      .strict(),
    material: z.boolean(),
  })
  .strict();

export const universalDecideAmendmentBodySchema = z
  .object({
    ...idempotencyField,
    amendmentId: protocolIdSchema,
    decision: z.enum(["ACCEPTED", "REJECTED"]),
    workerActorId: protocolIdSchema,
    reason: z.string().trim().min(1),
    authorityBasis: z.enum(["requester", "policy", "named_human"]),
    deploymentMode: z.enum(["REPLAY", "SHADOW", "REVIEW_GATED", "ENFORCED", "PAUSED"]).optional(),
    inputSnapshotDigest: sha256DigestSchema.optional(),
  })
  .strict();

export const universalAmendmentDecisionResponseSchema = z
  .object({
    decision: contractAmendmentDecisionSchema,
    successorContract: acceptedContractEnvelopeSchema.nullable(),
    supersededAttempt: workAttemptSchema.nullable(),
    replacementAttempt: workAttemptSchema.nullable(),
  })
  .strict();

export const universalRequestReviewBodySchema = z
  .object({
    ...idempotencyField,
    receiptId: protocolIdSchema,
    kind: z.enum(["HUMAN_CHECK", "APPEAL", "DISPUTE"]),
    reason: z.string().trim().min(1),
    assignedAuthorityActorId: protocolIdSchema.nullable().optional(),
    humanCheck: z
      .object({
        obligationId: protocolIdSchema,
        instruction: z.string().trim().min(1),
        evidenceArtifactIds: z.array(protocolIdSchema),
      })
      .strict()
      .nullable()
      .optional(),
  })
  .strict();

export const universalResolveReviewBodySchema = z
  .object({
    ...idempotencyField,
    reviewId: protocolIdSchema,
    outcome: z.enum([
      "NO_CHANGE",
      "NEW_RUN_REQUIRED",
      "SUCCESSOR_CONTRACT_REQUIRED",
      "OVERRIDE_REQUESTED",
    ]),
    humanDetermination: reviewResolutionSchema.shape.humanDetermination.optional(),
    reason: z.string().trim().min(1),
  })
  .strict();

export const universalProposeLearningBodySchema = z
  .object({
    ...idempotencyField,
    receiptId: protocolIdSchema,
    proposalKind: z.enum(["contract_successor", "policy_successor"]),
    target: learningProposalTargetSchema,
    proposal: canonicalJsonSchema,
  })
  .strict();

export const universalRailExecutionSchema = z
  .object({
    decision: enforcementDecisionSchema,
    intent: railActionIntentSchema,
    signal: railVerificationSignalSchema,
    adapterDelivery: universalAdapterDeliverySchema,
    effect: enforcementEffectSchema.nullable(),
  })
  .strict();

const overview: Operation<
  void,
  UniversalWorkReadQuery,
  void,
  z.infer<typeof universalVerificationOverviewResponseSchema>
> = {
  method: "GET",
  path: () => "/api/verification/universal",
  query: universalWorkReadQuerySchema,
  response: universalVerificationOverviewResponseSchema,
};

const trust: Operation<void, void, void, z.infer<typeof universalTrustMaterialSchema>> = {
  method: "GET",
  path: () => "/api/verification/universal/trust-root",
  response: universalTrustMaterialSchema,
};

const enrollProvider: Operation<
  void,
  void,
  z.infer<typeof signedProviderEnrollmentServiceRequestSchema>,
  z.infer<typeof signedProviderEnrollmentServiceResponseSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/environment-enrollment",
  body: signedProviderEnrollmentServiceRequestSchema,
  response: signedProviderEnrollmentServiceResponseSchema,
};

const work: Operation<
  z.infer<typeof universalWorkDetailParamsSchema>,
  UniversalWorkReadQuery,
  void,
  z.infer<typeof universalVerificationWorkDetailResponseSchema>
> = {
  method: "GET",
  path: ({ id }) => `/api/verification/universal/work/${encodeURIComponent(id)}`,
  params: universalWorkDetailParamsSchema,
  query: universalWorkReadQuerySchema,
  response: universalVerificationWorkDetailResponseSchema,
};

const receipt: Operation<
  z.infer<typeof receiptParams>,
  UniversalWorkReadQuery,
  void,
  z.infer<typeof universalVerificationReceiptDetailResponseSchema>
> = {
  method: "GET",
  path: ({ id }) => `/api/verification/universal/receipts/${encodeURIComponent(id)}`,
  params: receiptParams,
  query: universalWorkReadQuerySchema,
  response: universalVerificationReceiptDetailResponseSchema,
};

const createWork: Operation<
  void,
  UniversalWorkReadQuery | undefined,
  z.infer<typeof universalCreateWorkBodySchema>,
  z.infer<typeof universalWorkItemSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/work",
  query: universalWorkReadQuerySchema.optional(),
  body: universalCreateWorkBodySchema,
  response: universalWorkItemSchema,
  invalidates: [overview],
};

const acceptContract: Operation<
  void,
  void,
  z.infer<typeof universalAcceptContractBodySchema>,
  z.infer<typeof acceptedContractEnvelopeSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/contracts/accept",
  body: universalAcceptContractBodySchema,
  response: acceptedContractEnvelopeSchema,
  invalidates: [overview],
};

const compileContract: Operation<
  void,
  void,
  z.infer<typeof universalCompileContractBodySchema>,
  z.infer<typeof acceptedContractEnvelopeSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/contracts/compile",
  body: universalCompileContractBodySchema,
  response: acceptedContractEnvelopeSchema,
  invalidates: [overview],
};

const codingIntake: Operation<
  void,
  UniversalWorkReadQuery,
  z.infer<typeof universalCodingIntakeBodySchema>,
  z.infer<typeof universalCodingIntakeResponseSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/coding-intake",
  query: universalWorkReadQuerySchema,
  body: universalCodingIntakeBodySchema,
  response: universalCodingIntakeResponseSchema,
  invalidates: [overview],
};

const codingRepositorySelections: Operation<
  void,
  void,
  void,
  z.infer<typeof universalCodingRepositorySelectionsResponseSchema>
> = {
  method: "GET",
  path: () => "/api/verification/universal/coding-repositories",
  response: universalCodingRepositorySelectionsResponseSchema,
};

const acceptSourceReview: Operation<
  void,
  void,
  z.infer<typeof universalAcceptSourceReviewBodySchema>,
  z.infer<typeof acceptedContractEnvelopeSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/contracts/accept-source-review",
  body: universalAcceptSourceReviewBodySchema,
  response: acceptedContractEnvelopeSchema,
  invalidates: [overview],
};

const openAttempt: Operation<
  void,
  void,
  z.infer<typeof universalOpenAttemptBodySchema>,
  z.infer<typeof workAttemptSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/attempts/open",
  body: universalOpenAttemptBodySchema,
  response: workAttemptSchema,
  invalidates: [overview],
};

const submitAttempt: Operation<
  void,
  void,
  z.infer<typeof universalSubmitAttemptBodySchema>,
  z.infer<typeof workAttemptSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/attempts/submit",
  body: universalSubmitAttemptBodySchema,
  response: workAttemptSchema,
  invalidates: [overview],
};

const workerEnrollments: Operation<void, void, void, UniversalWorkerEnrollments> = {
  method: "GET",
  path: () => "/api/verification/universal/workers",
  response: universalWorkerEnrollmentsResponseSchema,
};

const registerWorkerPrincipal: Operation<
  void,
  void,
  z.infer<typeof universalRegisterWorkerPrincipalBodySchema>,
  z.infer<typeof universalRegisterWorkerPrincipalResponseSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/workers/register",
  body: universalRegisterWorkerPrincipalBodySchema,
  response: universalRegisterWorkerPrincipalResponseSchema,
  invalidates: [overview, workerEnrollments],
};

const issueWorkerDelegation: Operation<
  void,
  void,
  z.infer<typeof universalIssueWorkerDelegationBodySchema>,
  z.infer<typeof universalIssueWorkerDelegationResponseSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/workers/delegations/issue",
  body: universalIssueWorkerDelegationBodySchema,
  response: universalIssueWorkerDelegationResponseSchema,
  invalidates: [overview, workerEnrollments],
};

const workerAttachAttempt: Operation<
  void,
  void,
  z.infer<typeof universalWorkerAttachAttemptBodySchema>,
  z.infer<typeof universalWorkerAttemptCommandResponseSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/workers/attempts/attach",
  body: universalWorkerAttachAttemptBodySchema,
  response: universalWorkerAttemptCommandResponseSchema,
  invalidates: [overview, workerEnrollments],
};

const workerSubmitAttempt: Operation<
  void,
  void,
  z.infer<typeof universalWorkerSubmitAttemptBodySchema>,
  z.infer<typeof universalWorkerAttemptCommandResponseSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/workers/attempts/submit",
  body: universalWorkerSubmitAttemptBodySchema,
  response: universalWorkerAttemptCommandResponseSchema,
  invalidates: [overview, workerEnrollments],
};

const verify: Operation<
  void,
  void,
  z.infer<typeof universalVerifyCommandSchema>,
  z.infer<typeof universalVerifyResponseSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/verify",
  body: universalVerifyCommandSchema,
  // The response schema applies Zod defaults to additive oracle fields, so
  // its parse input is broader than its output. API responses arrive as
  // unknown JSON; preserve that boundary instead of requiring input/output
  // identity in Operation's generic parameter.
  response: universalVerifyResponseSchema as unknown as z.ZodType<
    z.infer<typeof universalVerifyResponseSchema>
  >,
  invalidates: [overview],
};

const codingEvidenceReplay: Operation<
  void,
  void,
  z.infer<typeof universalCodingEvidenceReplayBodySchema>,
  z.infer<typeof universalVerifyResponseSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/evidence/replay",
  body: universalCodingEvidenceReplayBodySchema,
  response: universalVerifyResponseSchema as unknown as z.ZodType<
    z.infer<typeof universalVerifyResponseSchema>
  >,
  invalidates: [overview],
};

const executeRail: Operation<
  void,
  void,
  z.infer<typeof universalExecuteRailBodySchema>,
  z.infer<typeof universalRailExecutionSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/rails/execute",
  body: universalExecuteRailBodySchema,
  response: universalRailExecutionSchema,
  invalidates: [overview],
};

const proposeAmendment: Operation<
  void,
  void,
  z.infer<typeof universalProposeAmendmentBodySchema>,
  z.infer<typeof contractAmendmentSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/amendments/propose",
  body: universalProposeAmendmentBodySchema,
  response: contractAmendmentSchema,
  invalidates: [overview],
};

const decideAmendment: Operation<
  void,
  void,
  z.infer<typeof universalDecideAmendmentBodySchema>,
  z.infer<typeof universalAmendmentDecisionResponseSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/amendments/decide",
  body: universalDecideAmendmentBodySchema,
  response: universalAmendmentDecisionResponseSchema,
  invalidates: [overview],
};

const requestReview: Operation<
  void,
  void,
  z.infer<typeof universalRequestReviewBodySchema>,
  z.infer<typeof reviewRequestSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/reviews/request",
  body: universalRequestReviewBodySchema,
  response: reviewRequestSchema,
  invalidates: [overview],
};

const resolveReview: Operation<
  void,
  void,
  z.infer<typeof universalResolveReviewBodySchema>,
  z.infer<typeof reviewResolutionSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/reviews/resolve",
  body: universalResolveReviewBodySchema,
  response: reviewResolutionSchema,
  invalidates: [overview],
};

const proposeLearning: Operation<
  void,
  void,
  z.infer<typeof universalProposeLearningBodySchema>,
  z.infer<typeof learningProposalSchema>
> = {
  method: "POST",
  path: () => "/api/verification/universal/learning/propose",
  body: universalProposeLearningBodySchema,
  response: learningProposalSchema,
  invalidates: [overview],
};

export type UniversalCreateWorkBody = z.infer<typeof universalCreateWorkBodySchema>;
export type UniversalAcceptContractBody = z.infer<typeof universalAcceptContractBodySchema>;
export type UniversalCompileContractBody = z.infer<typeof universalCompileContractBodySchema>;
export type UniversalCodingIntakeBody = z.infer<typeof universalCodingIntakeBodySchema>;
export type UniversalAcceptSourceReviewBody = z.infer<typeof universalAcceptSourceReviewBodySchema>;
export type UniversalOpenAttemptBody = z.infer<typeof universalOpenAttemptBodySchema>;
export type UniversalSubmitAttemptBody = z.infer<typeof universalSubmitAttemptBodySchema>;
export type UniversalRegisterWorkerPrincipalBody = z.infer<
  typeof universalRegisterWorkerPrincipalBodySchema
>;
export type UniversalIssueWorkerDelegationBody = z.infer<
  typeof universalIssueWorkerDelegationBodySchema
>;
export type UniversalWorkerAttachAttemptBody = z.infer<
  typeof universalWorkerAttachAttemptBodySchema
>;
export type UniversalWorkerSubmitAttemptBody = z.infer<
  typeof universalWorkerSubmitAttemptBodySchema
>;
export type UniversalCodingEvidenceReplayBody = z.infer<
  typeof universalCodingEvidenceReplayBodySchema
>;
export type UniversalExecuteRailBody = z.infer<typeof universalExecuteRailBodySchema>;
export type UniversalProposeAmendmentBody = z.infer<typeof universalProposeAmendmentBodySchema>;
export type UniversalDecideAmendmentBody = z.infer<typeof universalDecideAmendmentBodySchema>;
export type UniversalRequestReviewBody = z.infer<typeof universalRequestReviewBodySchema>;
export type UniversalResolveReviewBody = z.infer<typeof universalResolveReviewBodySchema>;
export type UniversalProposeLearningBody = z.infer<typeof universalProposeLearningBodySchema>;
export type UniversalRailExecution = z.infer<typeof universalRailExecutionSchema>;
export type UniversalVerdictReceipt = z.infer<typeof machineReadableVerdictReceiptSchema>;

export const universalVerification = {
  overview,
  enrollProvider,
  trust,
  work,
  receipt,
  createWork,
  acceptContract,
  compileContract,
  codingIntake,
  codingRepositorySelections,
  acceptSourceReview,
  registerWorkerPrincipal,
  workerEnrollments,
  openAttempt,
  submitAttempt,
  issueWorkerDelegation,
  workerAttachAttempt,
  workerSubmitAttempt,
  codingEvidenceReplay,
  verify,
  executeRail,
  proposeAmendment,
  decideAmendment,
  requestReview,
  resolveReview,
  proposeLearning,
};
